/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { alpha, useTheme } from '@mui/material/styles';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { formatEquityFieldValue } from 'util/equityhub';
import { EquityFieldKey, LegacyEquity } from 'types';
import { EQUITY_FIELD_LABEL_MAPPING } from 'config/equityhub';
import { useRecoilState, useRecoilValue } from 'recoil';
import { OPTIONS_CALCULATOR_ALL_FIELDS } from 'config/optionsCalculator';
import { formatAsCurrency, formatAsPercentage } from 'util/shared';
import { optionsCalculatorTickerCurrentFieldsState } from 'states/optionsCalculator';
import usePrices from 'hooks/equityhub/usePrices';
import { useQuery } from '@tanstack/react-query';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { SGTooltip } from 'components/core';
import { EQUITIES_TOOLTIP_MAP } from 'config/shared';

interface TickerPillProps {
  id: string;
  field: EquityFieldKey;
  value: number | string | boolean | undefined;
  onDelete: () => void;
  isHighlighted?: boolean;
}

const TickerPill: React.FC<TickerPillProps> = ({
  id,
  field,
  value,
  onDelete,
  isHighlighted,
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete();
  };

  return (
    <SGTooltip title={EQUITIES_TOOLTIP_MAP.get(field)}>
      <Chip
        ref={setNodeRef}
        style={style}
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: {
                  xs: 10,
                  md: 12,
                },
              }}
            >
              {EQUITY_FIELD_LABEL_MAPPING[field]}:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontSize: {
                  xs: 12,
                  md: 14,
                },
              }}
            >
              {formatEquityFieldValue(field, value)}
            </Typography>
          </Stack>
        }
        onDelete={handleDelete}
        deleteIcon={<CloseIcon fontSize="small" />}
        sx={{
          m: 0.5,
          p: 1,
          height: 'auto',
          cursor: 'grab',
          backgroundColor: isHighlighted
            ? alpha(theme.palette.warning.main, 0.15)
            : alpha(theme.palette.primary.main, 0.25),
          '&:hover': {
            backgroundColor: isHighlighted
              ? alpha(theme.palette.warning.main, 0.25)
              : alpha(theme.palette.primary.main, 0.35),
          },
          border: isHighlighted
            ? `1px solid ${alpha(theme.palette.warning.main, 0.6)}`
            : 'none',
          boxShadow: isHighlighted
            ? `0 0 4px ${alpha(theme.palette.warning.main, 0.3)}`
            : 'none',
        }}
        {...attributes}
        {...listeners}
      />
    </SGTooltip>
  );
};

const TickerOverview = () => {
  const theme = useTheme();

  const { sym, currentEquity } = useOptionsCalculatorData();
  const [displayedFields, setDisplayedFields] = useRecoilState(
    optionsCalculatorTickerCurrentFieldsState,
  );
  const [openModal, setOpenModal] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const posColor = useRecoilValue(positiveTrendColorState);
  const negColor = useRecoilValue(negativeTrendColorState);

  const [hiddenFields, setHiddenFields] = useState<EquityFieldKey[]>(() =>
    OPTIONS_CALCULATOR_ALL_FIELDS.filter(
      (field) => !displayedFields.includes(field),
    ),
  );

  const { getPrices } = usePrices({ bypassAccessCheck: true });

  // Use React Query to fetch prices
  const { data: pricePayload } = useQuery<Map<string, number>>({
    queryKey: ['prices', sym],
    queryFn: async () => {
      if (!sym) {
        return new Map<string, number>();
      }
      return getPrices([]);
    },
    enabled: !!sym,
    refetchOnWindowFocus: false,
  });

  const pricedEquity = useMemo(() => {
    if (currentEquity == null) {
      return null;
    }
    const price = pricePayload?.get(sym) ?? currentEquity.upx;
    return {
      ...currentEquity,
      [EquityFieldKey.price]: price,
      [EquityFieldKey.dailyChange]:
        price && currentEquity.upx
          ? (price - currentEquity.upx) / currentEquity.upx
          : undefined,
      [EquityFieldKey.prevClose]: currentEquity.upx
        ? currentEquity.upx
        : undefined,
    } as LegacyEquity;
  }, [currentEquity, pricePayload, sym]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDisplayedFields((fields) => {
        const oldIndex = fields.indexOf(active.id as EquityFieldKey);
        const newIndex = fields.indexOf(over.id as EquityFieldKey);
        return arrayMove(fields, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  };

  const handleRemoveField = (field: EquityFieldKey) => {
    setDisplayedFields((fields) => fields.filter((f) => f !== field));
    setHiddenFields((fields) => [...fields, field]);
  };

  const handleAddField = (field: EquityFieldKey) => {
    setHiddenFields((fields) => fields.filter((f) => f !== field));
    setDisplayedFields((fields) => [...fields, field]);
  };

  const isPriceUp = (pricedEquity?.[EquityFieldKey.dailyChange] ?? 0) >= 0;
  const priceChange =
    (pricedEquity?.[EquityFieldKey.price] ?? 0) -
      (pricedEquity?.[EquityFieldKey.prevClose] ?? 0) || 0;
  const percentChange = pricedEquity?.[EquityFieldKey.dailyChange] ?? 0;

  return (
    <>
      {pricedEquity && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton
            size="small"
            onClick={() => setOpenModal(true)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <EditIcon fontSize="small" color="primary" />
          </IconButton>
        </Box>
      )}

      <Stack spacing={3}>
        <Stack sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6" color="text.primary">
            {sym}
          </Typography>
          {pricedEquity && (
            <Typography variant="body2" color="text.secondary">
              {pricedEquity.name}
            </Typography>
          )}
        </Stack>

        {pricedEquity && (
          <>
            <Stack direction="row" alignItems="baseline" spacing={3}>
              <Typography
                fontWeight="bold"
                sx={{ fontSize: { xs: 12, md: 14 } }}
              >
                {formatAsCurrency(pricedEquity.price)}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body1"
                  color={isPriceUp ? posColor : negColor}
                  fontWeight="medium"
                  sx={{ fontSize: { xs: 12, md: 14 } }}
                >
                  {formatAsCurrency(priceChange)}
                </Typography>
                <Typography
                  variant="body1"
                  color={isPriceUp ? posColor : negColor}
                  fontWeight="medium"
                  sx={{ fontSize: { xs: 12, md: 14 } }}
                >
                  ({formatAsPercentage(percentChange)})
                </Typography>
                {isPriceUp ? (
                  <ArrowUpwardIcon fontSize="small" sx={{ color: posColor }} />
                ) : (
                  <ArrowDownwardIcon
                    fontSize="small"
                    sx={{ color: negColor }}
                  />
                )}
              </Stack>
            </Stack>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={displayedFields}
                strategy={horizontalListSortingStrategy}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    mt: 1,
                    gap: 2,
                  }}
                >
                  {displayedFields.map((field: EquityFieldKey) => (
                    <TickerPill
                      key={field}
                      id={field}
                      field={field}
                      value={pricedEquity?.[field as keyof LegacyEquity]}
                      onDelete={() => handleRemoveField(field)}
                      isHighlighted={
                        field === EquityFieldKey.earningsDate &&
                        pricedEquity?.[field] != null
                      }
                    />
                  ))}
                </Box>
              </SortableContext>
              <DragOverlay>
                {activeId ? (
                  <Chip
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {
                            EQUITY_FIELD_LABEL_MAPPING[
                              activeId as EquityFieldKey
                            ]
                          }
                          :
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatEquityFieldValue(
                            activeId as EquityFieldKey,
                            pricedEquity?.[activeId as keyof LegacyEquity],
                          )}
                        </Typography>
                      </Stack>
                    }
                    onDelete={() => {}}
                    deleteIcon={<CloseIcon fontSize="small" />}
                    sx={{
                      m: 0.5,
                      p: 1,
                      height: 'auto',
                      backgroundColor:
                        (activeId as EquityFieldKey) ===
                          EquityFieldKey.earningsDate &&
                        pricedEquity?.[EquityFieldKey.earningsDate] != null
                          ? alpha(theme.palette.warning.main, 0.15)
                          : alpha(theme.palette.primary.main, 0.25),
                      border:
                        (activeId as EquityFieldKey) ===
                          EquityFieldKey.earningsDate &&
                        pricedEquity?.[EquityFieldKey.earningsDate] != null
                          ? `1px solid ${alpha(theme.palette.warning.main, 0.6)}`
                          : 'none',
                      boxShadow:
                        (activeId as EquityFieldKey) ===
                          EquityFieldKey.earningsDate &&
                        pricedEquity?.[EquityFieldKey.earningsDate] != null
                          ? `0 0 4px ${alpha(theme.palette.warning.main, 0.3)}`
                          : 'none',
                    }}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>

            <Dialog
              open={openModal}
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Typography variant="h6" component="div">
                  Edit Displayed Fields
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Click on fields to add them to your ticker overview
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    Available Fields
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}
                  >
                    {hiddenFields.map((field) => (
                      <SGTooltip title={EQUITIES_TOOLTIP_MAP.get(field)}>
                        <Chip
                          key={field}
                          label={EQUITY_FIELD_LABEL_MAPPING[field]}
                          onClick={() => handleAddField(field)}
                          color="primary"
                          variant="outlined"
                          size="medium"
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08,
                              ),
                              borderColor: theme.palette.primary.main,
                            },
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                          }}
                        />
                      </SGTooltip>
                    ))}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  onClick={handleCloseModal}
                  variant="contained"
                  sx={{ minWidth: 100 }}
                >
                  Done
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Stack>
    </>
  );
};

export default TickerOverview;
